import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Send, AlertTriangle, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDefaultSender } from '@/hooks/useDefaultSender';
import type { MailMergeResult } from '@/components/leads/mailmerge/mailmergeExport';

interface MailMergeToCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCampaignCreated?: () => void;
  run: {
    id: string;
    name: string;
    outreach_type: string;
    results: any;
    success_count: number;
    failed_count: number;
    total_count: number;
  };
}

const MailMergeToCampaignDialog: React.FC<MailMergeToCampaignDialogProps> = ({ open, onOpenChange, onCampaignCreated, run }) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const { defaultSender } = useDefaultSender();
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState(run.name);
  const [senderSource, setSenderSource] = useState('system_sendgrid');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!profile?.company_id) return;
    supabase.from('companies').select('default_from_email').eq('id', profile.company_id).single()
      .then(({ data }) => setCompanyEmail(data?.default_from_email || null));
  }, [profile?.company_id]);

  const resolvedEmail = senderSource === 'system_sendgrid'
    ? companyEmail
    : senderSource === 'user_sendgrid'
      ? defaultSender?.from_email
      : fromEmail || null;

  const results: MailMergeResult[] = useMemo(
    () => (Array.isArray(run.results) ? run.results : []),
    [run.results]
  );

  const emailableResults = useMemo(
    () => results.filter(r => r.status === 'success' && r.emailBody && r.leadEmail),
    [results]
  );

  const skippedCount = results.length - emailableResults.length;

  const handleSend = async () => {
    if (emailableResults.length === 0) {
      toast.error('No emails to send');
      return;
    }

    if (senderSource === 'manual' && !fromEmail) {
      toast.error('Please enter a sender email');
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('convert-mailmerge-to-campaign', {
        body: {
          mailmerge_run_id: run.id,
          campaign_name: campaignName || run.name,
          sender_config: {
            source: senderSource,
            ...(senderSource === 'manual' ? { from_email: fromEmail, from_name: fromName } : {}),
          },
        },
      });

      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Conversion failed');

      toast.success(`Draft campaign created with ${data.queued_count} emails.`);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onCampaignCreated?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Create Draft Campaign
          </DialogTitle>
          <DialogDescription>
            Convert this mail merge into a draft email campaign. You can review, edit, and send from the Campaigns page.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4 px-[5px]">
            {/* Summary */}
            <div className="flex gap-3">
              <div className="flex-1 p-3 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                <div className="text-lg font-bold text-green-600">{emailableResults.length}</div>
                <div className="text-xs text-muted-foreground">Emails to send</div>
              </div>
              {skippedCount > 0 && (
                <div className="flex-1 p-3 rounded-lg bg-muted/40 border border-border/60 text-center">
                  <div className="text-lg font-bold text-muted-foreground">{skippedCount}</div>
                  <div className="text-xs text-muted-foreground">Skipped</div>
                </div>
              )}
            </div>

            {emailableResults.length === 0 && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                No successful email results to send.
              </div>
            )}

            {/* Campaign Name */}
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name">Campaign Name</Label>
              <Input
                id="campaign-name"
                value={campaignName}
                onChange={e => setCampaignName(e.target.value)}
                placeholder="Campaign name"
              />
            </div>

            {/* Sender Config */}
            <div className="space-y-1.5">
              <Label>Sender</Label>
              <Select value={senderSource} onValueChange={setSenderSource}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system_sendgrid">
                    <div className="flex flex-col items-start">
                      <span>Company Default</span>
                      <span className="text-xs text-muted-foreground">{companyEmail ? `Uses ${companyEmail}` : 'Company sending address'}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="user_sendgrid">
                    <div className="flex flex-col items-start">
                      <span>My Verified Email</span>
                      <span className="text-xs text-muted-foreground">{defaultSender?.from_email ? `Uses ${defaultSender.from_email}` : 'Your personal verified sender'}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="manual">
                    <div className="flex flex-col items-start">
                      <span>Custom Sender</span>
                      <span className="text-xs text-muted-foreground">Manually specify a from address</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {resolvedEmail && senderSource !== 'manual' && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Emails will be sent from: <span className="font-medium">{resolvedEmail}</span>
                </p>
              )}
            </div>

            {senderSource === 'manual' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="from-email">From Email</Label>
                  <Input
                    id="from-email"
                    type="email"
                    value={fromEmail}
                    onChange={e => setFromEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="from-name">From Name</Label>
                  <Input
                    id="from-name"
                    value={fromName}
                    onChange={e => setFromName(e.target.value)}
                    placeholder="Your Name"
                  />
                </div>
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || emailableResults.length === 0} className="gap-1.5">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Creating…' : `Create Draft (${emailableResults.length} emails)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MailMergeToCampaignDialog;
